/**
 * Grouped DataGrid row IDs shared by Workflow and document grouping.
 *
 * A group header is one or more `${field}:${encoded key}` segments joined by
 * `/`. A leaf is `${path}/${entityId}`.
 *
 * The key is percent-encoded because group keys are arbitrary column values: a
 * text answer or a board column name can contain both delimiters, which would
 * otherwise make two different group paths collide on one id. Grouping
 * documents by Name with one document called `Acme` and another called
 * `name:Acme` produced `name:Acme` and `name:name:Acme`, and the second parses
 * back to `Acme` — so a per-group select-all resolved its membership from the
 * wrong group and acted on the wrong documents, with no error anywhere.
 *
 * Encoded keys contain neither `/` nor `:`, and field identifiers are file
 * field names, chart dimensions or UUIDs, which contain neither either. So a
 * path parses back unambiguously with the field name at each depth
 * (`lastIndexOf(`${field}:`)`), and a leaf id splits on its last `/`.
 */

import type { WorkflowChartDimension } from './workflow-insights';

export const GROUP_ROW_PATH_SEPARATOR = '/' as const;
export const GROUP_ROW_FIELD_KEY_SEPARATOR = ':' as const;

export type GroupRowSegment<TField extends string> =
  `${TField}${typeof GROUP_ROW_FIELD_KEY_SEPARATOR}${string}`;

export type GroupRowPath<TField extends string> =
  | GroupRowSegment<TField>
  | `${GroupRowSegment<TField>}${typeof GROUP_ROW_PATH_SEPARATOR}${string}`;

export type GroupedLeafRowId<TField extends string> =
  `${GroupRowPath<TField>}${typeof GROUP_ROW_PATH_SEPARATOR}${string}`;

export type WorkflowGroupRowSegment = GroupRowSegment<WorkflowChartDimension>;
export type WorkflowGroupRowPath = GroupRowPath<WorkflowChartDimension>;
export type WorkflowGroupedLeafRowId = GroupedLeafRowId<WorkflowChartDimension>;

export interface EncodeGroupRowIdOptions {
  /**
   * Emit the key as-is instead of percent-encoded. Compatibility for report
   * grid bundles built before keys were encoded: they slice the key straight
   * out of the id and send it back as the group key on expand, so given
   * `parties:Acme%20Corp` they would ask for `Acme%20Corp`, match nothing, and
   * open the group empty. Such a client keeps the delimiter collision the
   * encoding fixed, which is its status quo. Goes away with the deprecated
   * `groupFields` argument, which is how the server tells those bundles apart.
   */
  rawKeys?: boolean;
}

export function encodeGroupRowSegment<TField extends string>(
  field: TField,
  key: string,
  { rawKeys = false }: EncodeGroupRowIdOptions = {}
): GroupRowSegment<TField> {
  const encodedKey = rawKeys ? key : encodeURIComponent(key);
  return `${field}${GROUP_ROW_FIELD_KEY_SEPARATOR}${encodedKey}`;
}

export function encodeGroupRowHeaderId<TField extends string>(
  field: TField,
  key: string,
  parentPath?: GroupRowPath<TField> | '' | null,
  options?: EncodeGroupRowIdOptions
): GroupRowPath<TField> {
  const segment = encodeGroupRowSegment(field, key, options);
  if (!parentPath) return segment;
  return `${parentPath}${GROUP_ROW_PATH_SEPARATOR}${segment}`;
}

export function encodeGroupRowPath<TField extends string>(
  groupFields: readonly [TField, ...TField[]],
  groupKeys: readonly [string, ...string[]],
  options?: EncodeGroupRowIdOptions
): GroupRowPath<TField>;
export function encodeGroupRowPath<TField extends string>(
  groupFields: readonly TField[],
  groupKeys: readonly string[],
  options?: EncodeGroupRowIdOptions
): GroupRowPath<TField> | '';
export function encodeGroupRowPath<TField extends string>(
  groupFields: readonly TField[],
  groupKeys: readonly string[],
  options?: EncodeGroupRowIdOptions
): GroupRowPath<TField> | '' {
  const firstField = groupFields[0];
  if (!firstField || !groupKeys.length) return '';

  let path: GroupRowPath<TField> | '' = '';
  for (const [index, key] of groupKeys.entries()) {
    const field = groupFields[index] ?? firstField;
    path = encodeGroupRowHeaderId(field, key, path, options);
  }
  return path;
}

export function encodeGroupedLeafRowId<TField extends string>(
  parentPath: GroupRowPath<TField>,
  entityId: string
): GroupedLeafRowId<TField>;
export function encodeGroupedLeafRowId<TField extends string>(
  parentPath: GroupRowPath<TField> | '',
  entityId: string
): GroupedLeafRowId<TField> | string;
export function encodeGroupedLeafRowId<TField extends string>(
  parentPath: GroupRowPath<TField> | '',
  entityId: string
): GroupedLeafRowId<TField> | string {
  if (!parentPath) return entityId;
  return `${parentPath}${GROUP_ROW_PATH_SEPARATOR}${entityId}`;
}

/**
 * Strips `${field}:` from a MUI groupKeys entry and decodes the key back to the
 * value the server grouped on. The entry may be a single segment
 * (`status:Draft`) or a nested path (`status:Draft/type:NDA`); either way the
 * key is everything after the last `${field}:`. Falls back to the first `:`
 * when the field prefix is missing, then to the raw string.
 *
 * The counterpart of `encodeGroupRowSegment` — the two have to move together or
 * the client sends group keys that match no row on the server.
 */
export function parseGroupKeyFromGroupRowId(
  rowId: string,
  field: string,
  options: ParseGroupRowIdOptions = {}
): string {
  const fieldPrefix = `${field}${GROUP_ROW_FIELD_KEY_SEPARATOR}`;
  const lastFieldIndex = rowId.lastIndexOf(fieldPrefix);
  if (lastFieldIndex >= 0)
    return decodeGroupRowKey(rowId.slice(lastFieldIndex + fieldPrefix.length), rowId, options);

  const colonIndex = rowId.indexOf(GROUP_ROW_FIELD_KEY_SEPARATOR);
  return decodeGroupRowKey(colonIndex >= 0 ? rowId.slice(colonIndex + 1) : rowId, rowId, options);
}

export interface ParseGroupRowIdOptions {
  /**
   * Called with the `URIError` and the offending row id when a key is not
   * valid escaping — an id this module did not build. The parse falls back to
   * the raw segment either way; this is the caller's chance to record it,
   * since the fallback key matches nothing on the server and the result is a
   * group that reads as empty. This module is shared with the server, so it
   * cannot reach a client error reporter itself.
   */
  onDecodeError?: (error: unknown, rowId: string) => void;
}

/**
 * Undoes `encodeGroupRowSegment`'s escaping. A malformed escape sequence means
 * an id this module did not build, so fall back to the segment as-is rather
 * than throwing out of a grid render — an unmatchable group key empties one
 * group, an exception takes down the whole grid.
 */
function decodeGroupRowKey(
  key: string,
  rowId: string,
  { onDecodeError }: ParseGroupRowIdOptions
): string {
  try {
    return decodeURIComponent(key);
  } catch (error) {
    onDecodeError?.(error, rowId);
    return key;
  }
}

export function parseGroupKeysFromGroupRowIds(
  groupRowIds: readonly unknown[] | null | undefined,
  groupFields: readonly string[],
  options: ParseGroupRowIdOptions = {}
): string[] {
  const firstField = groupFields[0];
  if (!groupRowIds?.length || !firstField) return [];

  return groupRowIds.map((rowId, index) =>
    parseGroupKeyFromGroupRowId(String(rowId), groupFields[index] ?? firstField, options)
  );
}

/** Workflow / document UUIDs do not contain `/`, so the leaf id is the last segment. */
export function parseEntityIdFromGroupedLeafRowId(rowId: string): string {
  const lastSlash = rowId.lastIndexOf(GROUP_ROW_PATH_SEPARATOR);
  if (lastSlash < 0) return rowId;
  return rowId.slice(lastSlash + 1);
}

export function parseParentPathFromGroupedLeafRowId(rowId: string): string {
  const lastSlash = rowId.lastIndexOf(GROUP_ROW_PATH_SEPARATOR);
  if (lastSlash < 0) return '';
  return rowId.slice(0, lastSlash);
}

export function getIsGroupRowIdOrDescendant(rowId: string, groupRowId: string): boolean {
  return rowId === groupRowId || rowId.startsWith(`${groupRowId}${GROUP_ROW_PATH_SEPARATOR}`);
}
